import React, { useEffect, useState, useRef } from "react";
import CompanyLayout from "../../components/company/CompanyLayout";
import FileButton from "../../components/ui/FileButton";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { toast } from "react-hot-toast";

export default function OrgManagementPage() {
  const [org, setOrg] = useState(null);
  const [myOrgs, setMyOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [myRole, setMyRole] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ email: "", role: "Member", department: "" });
  const [logoFile, setLogoFile] = useState(null);

  const [newDeptName, setNewDeptName] = useState("");
  const [renameDrafts, setRenameDrafts] = useState({});
  const ENABLE_DEPT_DELETE = false;

  const [salaryDrafts, setSalaryDrafts] = useState({});
  const [employmentDrafts, setEmploymentDrafts] = useState({});
  const [savingMemberId, setSavingMemberId] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvInputRef = useRef();

  const [transferMemberId, setTransferMemberId] = useState("");
  const [demoteCurrentOwner, setDemoteCurrentOwner] = useState(false);
  const [demoteToRole, setDemoteToRole] = useState("Manager");

  // Policy settings
  const [allowEmployeeContractorMode, setAllowEmployeeContractorMode] = useState(true);
  const [allowWorkerFlatFees, setAllowWorkerFlatFees] = useState(false);
  const [w2FeaturesEnabled, setW2FeaturesEnabled] = useState(true);
  const [manualTimeEntryEnabled, setManualTimeEntryEnabled] = useState(true);
  const [revenueTrackingEnabled, setRevenueTrackingEnabled] = useState(true);
  const [timecardAutoPay, setTimecardAutoPay] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Org-wide audit trail (role / employment type / salary / department changes,
  // ownership transfers, etc.). Owner / Admin / Manager only.
  const [activityLog, setActivityLog] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);

  // Business type
  const [businessType, setBusinessType] = useState("general");
  const [savingBusinessType, setSavingBusinessType] = useState(false);

  useEffect(() => {
    bootstrap().finally(() => setLoading(false));
  }, []);

  const bootstrap = async () => {
    try {
      // Fire first 2 requests in parallel
      const [me, mine] = await Promise.all([
        fetchWithAuth("/api/accounts/me/"),
        fetchWithAuth("/api/accounts/me/organizations/"),
      ]);
      setOrg(me.organization || null);
      setBusinessType(me.organization?.business_type || "general");
      setMyRole(me?.organization?.role || null);
      setTimecardAutoPay(!!me.organization?.timecard_auto_pay);
      setActiveOrgId(me.active_organization_id || (me.organization?.id ?? null));
      setMyOrgs(Array.isArray(mine?.organizations) ? mine.organizations : []);
      if (me.organization) {
        // Fire org-scoped requests in parallel
        await Promise.all([loadOrgScopedData(), loadBrandingSettings()]);
      } else {
        setDepartments([]); setMembers([]);
      }
    } catch (e) {
      toast.error("Failed to load organization.");
    }
  };

  const loadOrgScopedData = async () => {
    try {
      const [deptRes, membersRes, logRes] = await Promise.all([
        fetchWithAuth("/api/accounts/departments/"),
        fetchWithAuth("/api/accounts/org-members/"),
        // Activity log is best-effort — Owner/Admin/Manager only, but we
        // don't want a 403 on a Member view to nuke the whole page load.
        fetchWithAuth("/api/accounts/organization/logs/").catch(() => []),
      ]);
      setDepartments(Array.isArray(deptRes) ? deptRes : []);
      setMembers(Array.isArray(membersRes) ? membersRes : []);
      setActivityLog(Array.isArray(logRes) ? logRes : []);
    } catch {
      toast.error("Unable to load org data.");
    }
  };

  const loadBrandingSettings = async () => {
    try {
      const res = await fetchWithAuth("/api/accounts/org/customization/");
      // Load policy settings
      if (typeof res?.allow_employee_contractor_mode !== 'undefined') {
        setAllowEmployeeContractorMode(res.allow_employee_contractor_mode);
      }
      if (typeof res?.allow_worker_flat_fees !== 'undefined') {
        setAllowWorkerFlatFees(res.allow_worker_flat_fees);
      }
      if (typeof res?.w2_features_enabled !== 'undefined') {
        setW2FeaturesEnabled(res.w2_features_enabled);
      }
      if (typeof res?.manual_time_entry_enabled !== 'undefined') {
        setManualTimeEntryEnabled(res.manual_time_entry_enabled);
      }
      if (typeof res?.revenue_tracking_enabled !== 'undefined') {
        setRevenueTrackingEnabled(res.revenue_tracking_enabled);
      }
    } catch {
      // Endpoint may not exist yet - that's OK
      console.log("Branding endpoint not available");
    }
  };

  const handleSaveBusinessType = async (newType) => {
    if (!org?.id || newType === businessType) return;
    setSavingBusinessType(true);
    try {
      const res = await fetchWithAuth(`/api/accounts/organizations/${org.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_type: newType }),
      });
      const data = res?.json ? await res.json() : res;
      if (data?.error) throw new Error(data.error);
      setBusinessType(newType);
      toast.success("Business type updated. Refresh to see new feature options.");
    } catch (e) {
      toast.error(e?.message || "Failed to update business type");
    } finally {
      setSavingBusinessType(false);
    }
  };

  const handleSavePolicySetting = async (setting, value) => {
    setSavingPolicy(true);
    try {
      const res = await fetchWithAuth("/api/accounts/org/policy/", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        // Name the company on screen, so a tab that switched company elsewhere
        // cannot change a setting on a company this page is not showing.
        body: JSON.stringify({ [setting]: value, organization_id: org?.id }),
      });
      if (res?.success) {
        toast.success("Policy updated");
        if (setting === 'allow_employee_contractor_mode') {
          setAllowEmployeeContractorMode(value);
        }
        if (setting === 'allow_worker_flat_fees') {
          setAllowWorkerFlatFees(value);
        }
        if (setting === 'w2_features_enabled') {
          setW2FeaturesEnabled(value);
        }
        if (setting === 'manual_time_entry_enabled') {
          setManualTimeEntryEnabled(value);
        }
        if (setting === 'revenue_tracking_enabled') {
          setRevenueTrackingEnabled(value);
        }
        if (setting === 'timecard_auto_pay') {
          setTimecardAutoPay(value);
        }
      } else if (res?.error) {
        toast.error(res.error);
      }
    } catch {
      toast.error("Failed to update policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleSwitchOrg = async (newOrgId) => {
    try {
      await fetchWithAuth("/api/accounts/me/organizations/activate/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization_id: Number(newOrgId) }),
      });
      toast.success("Active organization updated.");
      await bootstrap();
    } catch {
      toast.error("Failed to switch organization.");
    }
  };

  const handleLeaveOrg = async () => {
    if (!org?.id) return;
    if (!window.confirm("Leave this organization?")) return;
    try {
      await fetchWithAuth("/api/accounts/leave-organization/", { method: "POST" });
      toast.success("You have left the organization.");
      await bootstrap();
    } catch (e) {
      if (e?.status === 409) toast.error("You are the only Owner. Transfer ownership first.");
      else toast.error("Failed to leave organization.");
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferMemberId) return toast.error("Choose a member first.");
    try {
      await fetchWithAuth(`/api/accounts/organizations/${org.id}/transfer-ownership/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_owner_member_id: Number(transferMemberId),
          demote_current_owner: demoteCurrentOwner,
          current_owner_new_role: demoteToRole,
        }),
      });
      toast.success("Ownership transferred.");
      setTransferMemberId("");
      await bootstrap();
    } catch {
      toast.error("Transfer failed.");
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth("/api/accounts/add-org-member/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      toast.success("Member added.");
      setForm({ email: "", role: "Member", department: "" });
      await loadOrgScopedData();
    } catch {
      toast.error("Failed to add member.");
    }
  };

  const handleBatchInvite = async () => {
    try {
      await fetchWithAuth("/api/accounts/add-org-members-batch/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emails: form.batch_emails || [],
          role: form.role,
          department: form.department || null,
        }),
      });
      toast.success("Invitations queued.");
      await loadOrgScopedData();
    } catch {
      toast.error("Batch invite failed.");
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    const fd = new FormData();
    fd.append("logo", logoFile);
    try {
      await fetchWithAuth("/api/accounts/upload-org-logo/", { method: "POST", body: fd });
      toast.success("Logo uploaded.");
      setLogoFile(null);
      await bootstrap();
    } catch {
      toast.error("Failed to upload logo.");
    }
  };

  const handleSaveMemberCompensation = async (m) => {
    const salaryRaw = salaryDrafts[m.id];
    const empRaw = employmentDrafts[m.id];
    const payload = { role: m.role };
    if (salaryRaw !== undefined && salaryRaw !== "") {
      const cleaned = String(salaryRaw).replace(/[$,\s]/g, "");
      const num = Number(cleaned);
      if (Number.isNaN(num) || num < 0) {
        toast.error("Invalid salary.");
        return;
      }
      payload.annual_salary = num;
    } else if (salaryRaw === "") {
      payload.annual_salary = 0;
    }
    if (empRaw) payload.employment_type = empRaw;

    setSavingMemberId(m.id);
    try {
      await fetchWithAuth(`/api/accounts/organization/members/${m.id}/update-role/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast.success("Compensation saved.");
      setSalaryDrafts((prev) => {
        const n = { ...prev };
        delete n[m.id];
        return n;
      });
      setEmploymentDrafts((prev) => {
        const n = { ...prev };
        delete n[m.id];
        return n;
      });
      await loadOrgScopedData();
    } catch (err) {
      // Surface the backend's reason — common ones now include
      // "Only Owners can change a member's role" (admin tried to change role)
      // or "Couldn't parse salary 'foo' as a number".
      toast.error(err?.message || "Failed to save compensation.");
    } finally {
      setSavingMemberId(null);
    }
  };

  const parseSalaryCsv = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const splitRow = (line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const header = splitRow(lines[0]).map((h) => h.toLowerCase());
    const emailIdx = header.indexOf("email");
    const salaryIdx = header.findIndex((h) => h === "annual_salary" || h === "salary");
    const empIdx = header.findIndex((h) => h === "employment_type" || h === "type");
    const hasHeader = emailIdx !== -1;
    const rows = [];
    const startIdx = hasHeader ? 1 : 0;
    for (let i = startIdx; i < lines.length; i++) {
      const cols = splitRow(lines[i]);
      const row = {};
      if (hasHeader) {
        row.email = cols[emailIdx] || "";
        if (salaryIdx !== -1) row.annual_salary = cols[salaryIdx] || "";
        if (empIdx !== -1) row.employment_type = cols[empIdx] || "";
      } else {
        row.email = cols[0] || "";
        row.annual_salary = cols[1] || "";
        if (cols[2]) row.employment_type = cols[2];
      }
      if (row.email) rows.push(row);
    }
    return rows;
  };

  const handleSalaryCsvUpload = async (file) => {
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const text = await file.text();
      const rows = parseSalaryCsv(text);
      if (!rows.length) {
        toast.error("No rows found in CSV.");
        return;
      }
      const res = await fetchWithAuth("/api/accounts/bulk-update-member-salaries/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows }),
      });
      setCsvResult(res);
      toast.success(`Updated ${res?.updated ?? 0} member(s).`);
      await loadOrgScopedData();
    } catch {
      toast.error("Bulk upload failed.");
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const downloadSalaryCsvTemplate = () => {
    const header = "email,annual_salary,employment_type";
    const sample = members.slice(0, 5).map((m) => `${m.email},${m.annual_salary ?? ""},${m.employment_type ?? ""}`);
    const body = [header, ...sample].join("\n");
    const blob = new Blob([body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "member_salaries_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    try {
      await fetchWithAuth("/api/accounts/departments/add/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newDeptName }),
      });
      toast.success("Department added.");
      setNewDeptName("");
      await loadOrgScopedData();
    } catch {
      toast.error("Failed to add department.");
    }
  };

  const handleRenameDepartment = async (deptId) => {
    const newName = (renameDrafts[deptId] || "").trim();
    if (!newName) return;
    try {
      await fetchWithAuth(`/api/accounts/departments/${deptId}/edit/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName }),
      });
      toast.success("Department renamed.");
      setRenameDrafts((prev) => ({ ...prev, [deptId]: "" }));
      await loadOrgScopedData();
    } catch {
      toast.error("Failed to rename department.");
    }
  };

  const owners = members.filter((m) => m.role === "Owner");
  const soleOwner = myRole === "Owner" && owners.length === 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-ink flex items-center justify-center">
        Loading…
      </div>
    );
  }

  return (
    <CompanyLayout org={org}>
      {/* Summary & Controls */}
      <section className="bg-surface border border-line rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-display italic text-ink">{org?.name || "Organization"}</h1>
            <p className="text-sm text-secondary">
              Role: {myRole || "-"} {soleOwner ? "(Sole Owner)" : ""}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select aria-label="Active organization"
              value={activeOrgId || ""}
              onChange={(e) => handleSwitchOrg(e.target.value)}
              className="bg-surface border border-line-control text-ink p-2 rounded text-sm focus:border-brand [color-scheme:light]"
            >
              {myOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {!soleOwner && (
              <button
                onClick={handleLeaveOrg}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-pill text-sm"
              >
                Leave Org
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Logo Upload */}
      <section className="bg-surface border border-line rounded-2xl p-5">
        <h2 className="text-xl font-display italic text-ink mb-4">Organization logo</h2>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {org?.logo && (
            <img src={org.logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-line" />
          )}
          <FileButton label="Choose a logo" accept="image/*" file={logoFile} onFile={setLogoFile} />
          <button onClick={handleUploadLogo} disabled={!logoFile} className="bg-brand text-white px-4 py-2 rounded-pill hover:bg-brand-hover disabled:opacity-50">
            Upload logo
          </button>
        </div>
        <p className="text-xs text-tertiary mt-3">Tip: Use a square PNG for best results.</p>
      </section>

      {/* =========== BUSINESS TYPE SECTION =========== */}
      {(myRole === "Owner" || myRole === "Admin") && (
        <section className="bg-surface border border-line rounded-2xl p-5">
          <h2 className="text-xl font-display italic text-ink mb-2">Business type</h2>
          <p className="text-sm text-secondary mb-4">
            Selecting your business type shows you only the features that apply to your industry.
            You can change this anytime.
          </p>
          <select aria-label="Business type"
            value={businessType}
            onChange={(e) => handleSaveBusinessType(e.target.value)}
            disabled={savingBusinessType}
            className="w-full sm:w-auto bg-surface border border-line-control rounded px-3 py-2 text-sm text-ink focus:border-brand [color-scheme:light]"
          >
            <option value="general">General / Other</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="staffing_agency">Staffing Agency</option>
            <option value="trades_services">Trades & Services (Roofing, HVAC, Plumbing, Electrical)</option>
            <option value="home_services">Home Services (Cleaning, Landscaping, Pest Control)</option>
            <option value="professional_services">Professional Services (Consulting, Legal, Accounting)</option>
            <option value="healthcare">Healthcare</option>
            <option value="hospitality">Hospitality / Events</option>
            <option value="transportation">Transportation / Trucking</option>
            <option value="creative">Creative Services (Photography, Design)</option>
          </select>
          {savingBusinessType && (
            <div className="text-xs text-faint mt-2">Saving...</div>
          )}
        </section>
      )}

      {/* =========== EMPLOYEE POLICIES SECTION =========== */}
      {(myRole === "Owner" || myRole === "Admin") && (
        <section className="bg-surface border border-line rounded-2xl p-5">
          <h2 className="text-xl font-display italic text-ink mb-4">Employee policies</h2>

          <div className="space-y-4">
            {/* Contractor Mode Toggle */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-contractor-mode" className="font-medium text-ink mb-1">Allow contractor mode</div>
                <p className="text-sm text-secondary">
                  When enabled, employees can also operate as independent contractors on the platform 
                  (take on their own contracts, receive payments, etc.). Disable to restrict employees 
                  to company work only.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-contractor-mode"
                  type="checkbox"
                  checked={allowEmployeeContractorMode}
                  onChange={(e) => handleSavePolicySetting('allow_employee_contractor_mode', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <p className="text-xs text-faint">
              Note: Changing this setting affects all current and future employees. Existing contractor
              profiles won't be deleted, but employees won't be able to access contractor features while
              employed by your organization.
            </p>

            {/* Worker-added flat fees (RT-K18): off by default, the company opts in. */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-worker-flat-fees" className="font-medium text-ink mb-1">Let workers add flat fees</div>
                <p className="text-sm text-secondary">
                  When on, the worker on a contract can add a flat fee, like an invoice, for your
                  approvers to review and pay. Every fee shows who added it. When off, only your
                  approvers add flat fees.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-worker-flat-fees"
                  type="checkbox"
                  checked={allowWorkerFlatFees}
                  onChange={(e) => handleSavePolicySetting('allow_worker_flat_fees', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            {/* Typed-in time toggle: off means people's own time comes only from the clock. */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-manual-time" className="font-medium text-ink mb-1">Let people type in their own time</div>
                <p className="text-sm text-secondary">
                  When off, your people record their time only by clocking in and out, which records
                  Sift&rsquo;s server time, their internet address and device. They can&rsquo;t type in hours
                  themselves. You and your admins can still enter time for them.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-manual-time"
                  type="checkbox"
                  checked={manualTimeEntryEnabled}
                  onChange={(e) => handleSavePolicySetting('manual_time_entry_enabled', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            {/* W-2 Features Toggle */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-w2-features" className="font-medium text-ink mb-1">Enable W-2 Features</div>
                <p className="text-sm text-secondary">
                  Master switch for W-2 functionality. When enabled, your org can run W-2
                  employees through Sift (Bulk Payroll Export to ADP/Gusto/Paychex, the W-2
                  employment-type option for new members, and the W-2 toggle inside the Credential
                  Center). When off, those surfaces are hidden across the whole org. Existing W-2 data
                  stays in place, this only changes the UI surfaces. Internal Assignments stay on
                  either way: they are for anyone on your team, W-2 or not.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-w2-features"
                  type="checkbox"
                  checked={w2FeaturesEnabled}
                  onChange={(e) => handleSavePolicySetting('w2_features_enabled', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <p className="text-xs text-faint">
              Tip: leave this on for the demo so the Bulk Payroll Export button and the Credential
              Center W-2 toggle render. Existing customers who only use Sift for 1099 contractors can
              switch this off to declutter.
            </p>

            {/* Revenue tracking toggle */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-track-revenue" className="font-medium text-ink mb-1">Track Revenue / P&amp;L</div>
                <p className="text-sm text-secondary">
                  Leave on if you bill clients through Sift (agency placements, fractional pros under
                  client engagements). Switch off if Sift is just tracking your own labor cost, for
                  manufacturers running W-2 + 1099 internally with no client side, all labor is expense
                  and a revenue number is meaningless. When off: the Income Statement sidebar entry
                  is hidden and the Reporting Center collapses to expense + projection only.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-track-revenue"
                  type="checkbox"
                  checked={revenueTrackingEnabled}
                  onChange={(e) => handleSavePolicySetting('revenue_tracking_enabled', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
          </div>
        </section>
      )}

      {/* =========== BILLING & PAYMENTS SECTION =========== */}
      {(myRole === "Owner" || myRole === "Admin") && (
        <section className="bg-surface border border-line rounded-2xl p-5">
          <h2 className="text-xl font-display italic text-ink mb-4">Billing &amp; Payments</h2>

          <div className="space-y-4">
            {/* Auto-pay approved timecards */}
            <div className="flex items-start justify-between gap-3 p-4 bg-sunken border border-line rounded-lg">
              <div className="flex-1 pr-4">
                <div id="org-management-page-auto-pay" className="font-medium text-ink mb-1">Auto-pay approved timecards</div>
                <p className="text-sm text-secondary">
                  When enabled, approving a timecard immediately charges your saved
                  card. No separate "Pay" step. Requires a payment method on file
                  (the first payment still runs through checkout to capture a card).
                  W-2 timecards are excluded, they settle through Bulk Payroll Export.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input aria-labelledby="org-management-page-auto-pay"
                  type="checkbox"
                  checked={timecardAutoPay}
                  onChange={(e) => handleSavePolicySetting('timecard_auto_pay', e.target.checked)}
                  disabled={savingPolicy}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-line-control peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <p className="text-xs text-faint">
              If a charge fails (declined card, contractor not yet connected to Stripe),
              the timecard stays approved-but-unpaid and you can pay it manually from the
              Timecards dashboard.
            </p>
          </div>
        </section>
      )}

      {/* Departments */}
      <section className="bg-surface border border-line rounded-2xl p-5">
        <h2 className="text-xl font-display italic text-ink mb-4">Departments</h2>
        <form onSubmit={handleAddDepartment} className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            aria-label="New department name"
            type="text"
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            placeholder="New department name"
            className="flex-1 bg-surface border border-line-control text-ink placeholder-faint focus:border-brand p-2 rounded [color-scheme:light]"
          />
          <button type="submit" className="bg-brand text-white px-4 py-2 rounded-pill hover:bg-brand-hover">
            Add
          </button>
        </form>
        <ul className="space-y-2">
          {departments.length === 0 && <li className="text-sm text-secondary">No departments yet.</li>}
          {departments.map((d) => (
            <li key={d.id} className="flex items-center gap-2 bg-sunken border border-line p-2 rounded">
              <input
                aria-label={`Rename department ${d.name}`}
                type="text"
                value={renameDrafts[d.id] ?? d.name}
                onChange={(e) => setRenameDrafts((prev) => ({ ...prev, [d.id]: e.target.value }))}
                className="flex-1 bg-surface border border-line-control text-ink focus:border-brand p-2 rounded text-sm [color-scheme:light]"
              />
              <button
                onClick={() => handleRenameDepartment(d.id)}
                className="text-xs bg-brand text-white px-3 py-2 rounded-pill hover:bg-brand-hover"
              >
                Save
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Members + Transfer Ownership */}
      <section className="bg-surface border border-line rounded-2xl p-5">
        <h2 className="text-xl font-display italic text-ink mb-4">Members</h2>

        {/* Single add */}
        <form onSubmit={(e) => handleAddMember(e)} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 items-end">
          <input
            aria-label="Email"
            type="email"
            name="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email"
            className="bg-surface border border-line-control text-ink placeholder-faint focus:border-brand p-2 rounded [color-scheme:light]"
            required
          />
          <select aria-label="Role"
            name="role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded [color-scheme:light]"
          >
            <option value="Authorized User">Authorized User</option>
            <option value="Admin">Admin</option>
            <option value="Manager">Manager</option>
            <option value="Member">Member</option>
          </select>
          <select aria-label="Department"
            name="department"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded [color-scheme:light]"
          >
            <option value="">No Department</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button type="submit" className="bg-brand text-white px-4 py-2 rounded-pill hover:bg-brand-hover">
            Add
          </button>
        </form>

        {/* Batch invite */}
        <div className="mt-8 border-t border-line pt-6">
          <h3 className="font-semibold text-ink mb-2">Batch invite</h3>
          <p id="batch-invite-hint" className="text-xs text-secondary mb-2">Paste emails separated by commas, spaces, or new lines.</p>
          <textarea
            aria-label="Emails to invite"
            aria-describedby="batch-invite-hint"
            rows={4}
            className="w-full bg-surface border border-line-control text-ink placeholder-faint focus:border-brand p-2 rounded text-sm [color-scheme:light]"
            placeholder="a@x.com, b@y.com, c@z.com"
            onChange={(e) => {
              const raw = e.target.value || "";
              const emails = raw.split(/[\s,]+/).filter(Boolean);
              setForm((f) => ({ ...f, batch_emails: emails }));
            }}
          />
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <select aria-label="Role for these invites"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded [color-scheme:light]"
            >
              <option value="Authorized User">Authorized User</option>
              <option value="Admin">Admin</option>
              <option value="Manager">Manager</option>
              <option value="Member">Member</option>
            </select>
            <select aria-label="Department for these invites"
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded [color-scheme:light]"
            >
              <option value="">No Department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <button onClick={handleBatchInvite} className="bg-brand text-white px-4 py-2 rounded-pill hover:bg-brand-hover">
              Send Invites
            </button>
          </div>
        </div>

        {/* Bulk salary CSV upload (Owner only) */}
        {myRole === "Owner" && (
          <div className="mt-8 border-t border-line pt-6">
            <h3 className="font-semibold text-ink mb-2">Bulk Salary Upload (CSV)</h3>
            <p className="text-xs text-secondary mb-3">
              Upload a CSV with columns <code className="text-ink">email,annual_salary,employment_type</code>.
              <code className="text-ink"> employment_type</code> must be <code className="text-ink">1099</code> or <code className="text-ink">W-2</code> (optional).
              Members are matched by email within this organization.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center text-sm cursor-pointer">
                <span className="border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill hover:bg-sunken">
                  Upload salary CSV
                </span>
                <input
                  aria-label="Salary CSV file"
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleSalaryCsvUpload(e.target.files?.[0])}
                  disabled={csvUploading}
                  className="sr-only"
                />
              </label>
              <button
                type="button"
                onClick={downloadSalaryCsvTemplate}
                className="border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill text-sm hover:border-brand hover:text-brand"
              >
                Download template
              </button>
              {csvUploading && <span className="text-xs text-secondary">Uploading…</span>}
            </div>
            {csvResult && (
              <div className="mt-3 text-xs text-secondary">
                Updated <span className="text-ink">{csvResult.updated ?? 0}</span>
                {Array.isArray(csvResult.skipped) && csvResult.skipped.length > 0 && (
                  <>
                    , skipped <span className="text-ink">{csvResult.skipped.length}</span>:
                    <ul className="mt-1 list-disc list-inside text-faint">
                      {csvResult.skipped.slice(0, 10).map((s, i) => (
                        <li key={i}>
                          {s.email || `row ${s.row}`} · {s.reason}
                        </li>
                      ))}
                      {csvResult.skipped.length > 10 && <li>…and {csvResult.skipped.length - 10} more</li>}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Member list */}
        <div className="mt-8">
          <h3 className="font-semibold text-ink mb-2">Current members</h3>
          <ul className="space-y-2 text-sm">
            {members.length === 0 && <li className="text-secondary">No members yet.</li>}
            {members.map((m) => {
              const canEditComp = myRole === "Owner";
              const salaryVal = salaryDrafts[m.id] !== undefined
                ? salaryDrafts[m.id]
                : (m.annual_salary ?? "");
              const empVal = employmentDrafts[m.id] !== undefined
                ? employmentDrafts[m.id]
                : (m.employment_type || "1099");
              const isDirty = salaryDrafts[m.id] !== undefined || employmentDrafts[m.id] !== undefined;
              return (
                <li key={m.id} className="border border-line p-3 rounded bg-sunken">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="min-w-0 break-words text-ink">
                        {m.name || m.email} · {m.role} {m.department ? `(${m.department})` : ""}
                      </div>
                      {m.name && <div className="text-xs text-faint truncate">{m.email}</div>}
                    </div>
                    {canEditComp && (
                      <div className="flex flex-wrap items-center gap-2">
                        <select aria-label={`Worker type for ${m.name || m.email}`}
                          value={empVal}
                          onChange={(e) =>
                            setEmploymentDrafts((prev) => ({ ...prev, [m.id]: e.target.value }))
                          }
                          className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded text-xs [color-scheme:light]"
                        >
                          <option value="1099">1099</option>
                          <option value="W-2">W-2</option>
                        </select>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-faint text-xs">$</span>
                          <input
                            aria-label={`Annual salary for ${m.name || m.email}`}
                            type="number"
                            min="0"
                            step="1000"
                            value={salaryVal ?? ""}
                            placeholder="Annual salary"
                            onChange={(e) =>
                              setSalaryDrafts((prev) => ({ ...prev, [m.id]: e.target.value }))
                            }
                            className="bg-surface border border-line-control text-ink placeholder-faint focus:border-brand pl-5 pr-2 py-2 rounded text-xs w-36 [color-scheme:light]"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={!isDirty || savingMemberId === m.id}
                          onClick={() => handleSaveMemberCompensation(m)}
                          className={`px-3 py-2 rounded-pill text-xs ${
                            isDirty && savingMemberId !== m.id
                              ? "bg-brand text-white hover:bg-brand-hover"
                              : "bg-sunken text-faint border border-line cursor-not-allowed"
                          }`}
                        >
                          {savingMemberId === m.id ? "Saving…" : "Save"}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Transfer Ownership */}
        {myRole === "Owner" && (
          <div className="mt-8 border-t border-line pt-6">
            <h3 className="font-semibold text-ink mb-2">Transfer Ownership</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select aria-label="Member to transfer ownership to"
                value={transferMemberId}
                onChange={(e) => setTransferMemberId(e.target.value)}
                className="bg-surface border border-line-control text-ink focus:border-brand p-2 rounded text-sm [color-scheme:light]"
              >
                <option value="">Select member…</option>
                {members.filter((m) => m.role !== "Owner").map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email} · {m.role}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="accent-brand"
                  checked={demoteCurrentOwner}
                  onChange={(e) => setDemoteCurrentOwner(e.target.checked)}
                />
                Demote me after transfer
              </label>

              <select aria-label="Your role after the transfer"
                value={demoteToRole}
                onChange={(e) => setDemoteToRole(e.target.value)}
                disabled={!demoteCurrentOwner}
                className={`bg-surface border border-line-control text-ink focus:border-brand p-2 rounded text-sm [color-scheme:light] ${
                  !demoteCurrentOwner ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <option value="Manager">Manager</option>
                <option value="Admin">Admin</option>
                <option value="Member">Member</option>
                <option value="Authorized User">Authorized User</option>
              </select>
            </div>

            <div className="mt-3">
              <button
                onClick={handleTransferOwnership}
                disabled={!transferMemberId}
                className={`px-4 py-2 rounded-pill ${
                  transferMemberId ? "bg-brand text-white hover:bg-brand-hover" : "bg-sunken text-faint border border-line cursor-not-allowed"
                }`}
              >
                Confirm Transfer
              </button>
            </div>
          </div>
        )}
      </section>

      {/* =========== RECENT ACTIVITY (audit trail) =========== */}
      {(myRole === "Owner" || myRole === "Admin" || myRole === "Manager") && (
        <section className="bg-surface border border-line rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-display italic text-ink">Recent activity</h2>
              <p className="text-sm text-secondary mt-1">
                Audit trail of role, employment-type, salary, and department
                changes \u2014 plus ownership transfers and member departures.
                Every entry records who made the change and when.
              </p>
            </div>
            {activityLog.length > 5 && (
              <button
                onClick={() => setShowAllActivity((v) => !v)}
                className="text-xs px-3 py-1.5 rounded-pill border border-line-strong bg-surface text-ink hover:border-brand hover:text-brand self-start"
              >
                {showAllActivity ? "Show fewer" : `Show all (${activityLog.length})`}
              </button>
            )}
          </div>

          {activityLog.length === 0 ? (
            <p className="text-sm text-faint">No recent activity yet.</p>
          ) : (
            <div className="border border-line rounded-lg divide-y divide-line overflow-hidden">
              {(showAllActivity ? activityLog : activityLog.slice(0, 5)).map((log, i) => (
                <div key={i} className="px-4 py-3 hover:bg-sunken">
                  <div className="text-sm text-secondary">
                    <span className="font-semibold text-ink">{log.actor || "Unknown"}</span>{" "}
                    <span className="text-secondary">{log.action}</span>
                    {log.target && (
                      <>
                        {" \u2014 "}
                        <span className="text-tertiary">{log.target}</span>
                      </>
                    )}
                  </div>
                  <div className="text-[0.6875rem] text-faint mt-0.5">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </CompanyLayout>
  );
}